import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2,
  Download, Printer, Search, X, Loader2, FileWarning, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// Worker — served from /public so the URL is stable & relative to the app.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Secure in-app PDF viewer.
 *
 * Props
 * -----
 * - viewUrl: relative URL returned by /view-link (already token-gated).
 * - fileName: display name (used on download).
 * - mime: original mime (used for fallback rendering).
 * - canDownload / canPrint: gated by caller's role/permissions.
 * - documentId: optional — used to log print audit events.
 * - onPrint / onDownload: optional client-side hooks (analytics).
 */
export default function PdfViewer({
  viewUrl, fileName = 'document.pdf', mime = 'application/pdf',
  canDownload = true, canPrint = true, documentId = null,
  className = '',
}) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [matchedPages, setMatchedPages] = useState([]);
  const [pageWidth, setPageWidth] = useState(800);
  const [absoluteUrl, setAbsoluteUrl] = useState('');

  // Resolve absolute URL once — react-pdf needs a full URL to load via fetch.
  useEffect(() => {
    if (!viewUrl) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const full = viewUrl.startsWith('http') ? viewUrl : `${base}${viewUrl}`;
    setAbsoluteUrl(full);
  }, [viewUrl]);

  // Resize observer for responsive page width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      // Reserve padding (24px) + small buffer for scrollbar.
      const w = Math.max(280, entry.contentRect.width - 32);
      setPageWidth(Math.min(w, 1100));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fullscreen state sync (escape key, etc.)
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const pdfRef = useRef(null);
  const onLoadSuccess = useCallback((pdf) => {
    pdfRef.current = pdf;
    setNumPages(pdf.numPages);
    setLoading(false);
  }, []);
  const onLoadError = useCallback((e) => {
    setError(e?.message || 'Could not load the PDF');
    setLoading(false);
  }, []);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') setPageNumber((p) => Math.max(1, p - 1));
      else if (e.key === 'ArrowRight') setPageNumber((p) => Math.min(numPages, p + 1));
      else if (e.key === '+' || e.key === '=') setScale((s) => Math.min(3, +(s + 0.1).toFixed(2)));
      else if (e.key === '-') setScale((s) => Math.max(0.4, +(s - 0.1).toFixed(2)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [numPages]);

  // Search — reuses the already-loaded PDF instance to avoid a second fetch
  // (and a second audit row) per search query.
  const runSearch = async () => {
    if (!searchTerm.trim() || !pdfRef.current) { setMatchedPages([]); return; }
    try {
      const pdf = pdfRef.current;
      const term = searchTerm.toLowerCase();
      const found = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = await pdf.getPage(i);
        const txt = await page.getTextContent();
        const text = txt.items.map((it) => it.str).join(' ').toLowerCase();
        if (text.includes(term)) found.push(i);
      }
      setMatchedPages(found);
      if (found.length === 0) toast.info('No matches found in this PDF');
      else { setPageNumber(found[0]); toast.success(`Found in ${found.length} page${found.length === 1 ? '' : 's'}`); }
    } catch {
      toast.error('Search failed');
    }
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen();
      else await document.exitFullscreen();
    } catch { /* user cancelled */ }
  };

  const handleDownload = () => {
    if (!canDownload) return;
    const a = document.createElement('a');
    a.href = absoluteUrl + (absoluteUrl.includes('?') ? '&' : '?') + 'dl=1';
    // Force browser to use download attribute. Backend still issues inline
    // because the token is mode=view; the anchor download attr is just a hint.
    a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handlePrint = async () => {
    if (!canPrint) return;
    try {
      const resp = await fetch(absoluteUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
      w.addEventListener('load', () => { try { w.print(); } catch { /* */ } });
      if (documentId) {
        try { await api.auditPrint(documentId); } catch { /* non-fatal */ }
      }
    } catch {
      toast.error('Could not prepare PDF for printing');
    }
  };

  const isPdf = useMemo(() => /pdf/i.test(mime), [mime]);

  // Unsupported preview fallback — download CTA only.
  if (!isPdf) {
    return (
      <div data-testid="pdf-viewer-unsupported"
           className={`rounded-xl border border-neutral-200 bg-neutral-50 p-10 text-center ${className}`}>
        <FileWarning className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
        <p className="text-[14.5px] font-semibold text-[#14110f]">
          In-app preview is not available for this file type.
        </p>
        <p className="text-[12.5px] text-neutral-500 mt-1">{fileName} · {mime}</p>
        {canDownload && (
          <button data-testid="fallback-download-btn" onClick={handleDownload}
                  className="mt-5 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px]">
            <FileDown className="w-4 h-4" />Download file
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef}
         data-testid="pdf-viewer"
         className={`rounded-xl border border-neutral-200 bg-neutral-100 overflow-hidden flex flex-col ${className} ${fullscreen ? 'bg-black' : ''}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 sm:px-3 py-2 bg-[#14110f] text-white text-[12px]">
        <div className="flex items-center gap-1">
          <button data-testid="pdf-prev" disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  className="p-1.5 rounded hover:bg-white/10 disabled:opacity-40">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input data-testid="pdf-page-input"
                 type="number" min={1} max={numPages || 1}
                 value={pageNumber}
                 onChange={(e) => {
                   const n = parseInt(e.target.value, 10);
                   if (!Number.isNaN(n)) setPageNumber(Math.max(1, Math.min(numPages || 1, n)));
                 }}
                 className="w-12 text-center bg-white/10 rounded px-1 py-1 text-[12px] outline-none" />
          <span className="opacity-70">/ {numPages || '—'}</span>
          <button data-testid="pdf-next" disabled={pageNumber >= numPages}
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  className="p-1.5 rounded hover:bg-white/10 disabled:opacity-40">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <span className="opacity-30 mx-1">|</span>

        <button data-testid="pdf-zoom-out"
                onClick={() => setScale((s) => Math.max(0.4, +(s - 0.1).toFixed(2)))}
                className="p-1.5 rounded hover:bg-white/10">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="px-1 tabular-nums opacity-70" data-testid="pdf-zoom-value">{Math.round(scale * 100)}%</span>
        <button data-testid="pdf-zoom-in"
                onClick={() => setScale((s) => Math.min(3, +(s + 0.1).toFixed(2)))}
                className="p-1.5 rounded hover:bg-white/10">
          <ZoomIn className="w-4 h-4" />
        </button>

        <span className="opacity-30 mx-1">|</span>

        <button data-testid="pdf-rotate" onClick={() => setRotation((r) => (r + 90) % 360)}
                className="p-1.5 rounded hover:bg-white/10" title="Rotate">
          <RotateCw className="w-4 h-4" />
        </button>
        <button data-testid="pdf-toggle-search"
                onClick={() => setSearchOpen((v) => !v)}
                className="p-1.5 rounded hover:bg-white/10" title="Search">
          <Search className="w-4 h-4" />
        </button>
        <button data-testid="pdf-fullscreen" onClick={toggleFullscreen}
                className="p-1.5 rounded hover:bg-white/10" title="Fullscreen">
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>

        <span className="flex-1" />

        {canPrint && (
          <button data-testid="pdf-print" onClick={handlePrint}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10" title="Print">
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Print</span>
          </button>
        )}
        {canDownload && (
          <button data-testid="pdf-download" onClick={handleDownload}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10" title="Download">
            <Download className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Download</span>
          </button>
        )}
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="flex items-center gap-2 bg-neutral-900 text-white px-3 py-2 text-[12.5px]">
          <input data-testid="pdf-search-input" autoFocus value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                 placeholder="Search PDF text…"
                 className="flex-1 bg-white/10 px-2 py-1.5 rounded outline-none text-white placeholder:text-white/50" />
          <button data-testid="pdf-search-run" onClick={runSearch}
                  className="px-3 py-1.5 rounded bg-[#f5b300] text-[#14110f] font-semibold">Find</button>
          {matchedPages.length > 0 && (
            <span className="opacity-80">Matches on pages {matchedPages.slice(0, 10).join(', ')}{matchedPages.length > 10 ? '…' : ''}</span>
          )}
          <button onClick={() => { setSearchOpen(false); setSearchTerm(''); setMatchedPages([]); }}
                  className="p-1 rounded hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* PDF canvas */}
      <div className="flex-1 overflow-auto flex items-start justify-center py-4">
        {error ? (
          <div className="text-center text-rose-600 text-[13.5px] py-10">
            <FileWarning className="w-8 h-8 mx-auto mb-2" />
            {error}
          </div>
        ) : !absoluteUrl ? null : (
          <Document file={absoluteUrl}
                    onLoadSuccess={onLoadSuccess}
                    onLoadError={onLoadError}
                    loading={(
                      <div className="text-neutral-400 py-10 flex items-center gap-2 text-[13px]">
                        <Loader2 className="w-4 h-4 animate-spin" />Loading PDF…
                      </div>
                    )}>
            <Page pageNumber={pageNumber} scale={scale} rotate={rotation}
                  width={pageWidth}
                  renderAnnotationLayer
                  renderTextLayer
                  loading={loading ? (
                    <div className="text-neutral-400 py-10 text-[12.5px]">Rendering page…</div>
                  ) : null}
                  className="shadow-lg bg-white" />
          </Document>
        )}
      </div>
    </div>
  );
}
